import { z } from 'zod';
/**
 * Énumérations du domaine. Ces valeurs sont dupliquées dans `schema.prisma` — Prisma ne
 * sait pas les importer d'un paquet TypeScript. Le test `enums.test.ts` vérifie que les deux
 * définitions ne divergent pas.
 */
/** Rôle au sein d'une organisation (PRD §2.1). Ordre croissant de privilège. */
export const OrgRole = {
    VIEWER: 'VIEWER',
    ANNOTATOR: 'ANNOTATOR',
    EDITOR: 'EDITOR',
    ADMIN: 'ADMIN',
    OWNER: 'OWNER',
};
export const orgRoleSchema = z.nativeEnum(OrgRole);
/** Hiérarchie des rôles : un rôle donné hérite des droits de tous ceux qui le précèdent. */
export const ORG_ROLE_HIERARCHY = [
    OrgRole.VIEWER,
    OrgRole.ANNOTATOR,
    OrgRole.EDITOR,
    OrgRole.ADMIN,
    OrgRole.OWNER,
];
/** `true` si `role` confère au moins les droits de `required`. */
export function roleAtLeast(role, required) {
    return ORG_ROLE_HIERARCHY.indexOf(role) >= ORG_ROLE_HIERARCHY.indexOf(required);
}
/**
 * Rôle **plateforme** (ADR-0025), orthogonal aux rôles d'organisation. La très grande majorité des
 * comptes sont `USER` (aucun privilège transverse). `SUPERADMIN` ouvre la console d'administration
 * qui voit *toutes* les organisations — l'unique exception, assumée et journalisée, au cloisonnement
 * (CLAUDE.md §1). `SUPPORT` (lecture seule) est **réservé** : nommé pour qu'un incrément ultérieur
 * l'ajoute sans migration, mais non émis à ce stade.
 */
export const PlatformRole = {
    USER: 'USER',
    SUPPORT: 'SUPPORT',
    SUPERADMIN: 'SUPERADMIN',
};
export const platformRoleSchema = z.nativeEnum(PlatformRole);
/** Ordre croissant de privilège plateforme. `USER` n'accède à aucune surface d'administration. */
export const PLATFORM_ROLE_HIERARCHY = [
    PlatformRole.USER,
    PlatformRole.SUPPORT,
    PlatformRole.SUPERADMIN,
];
/** `true` si `role` plateforme confère au moins les droits de `required`. */
export function platformRoleAtLeast(role, required) {
    return PLATFORM_ROLE_HIERARCHY.indexOf(role) >= PLATFORM_ROLE_HIERARCHY.indexOf(required);
}
/**
 * Persona d'interface (ADR-0031) : le **métier** que l'utilisateur déclare, choisi par lui. C'est une
 * **préférence d'affichage** — elle ne fait que décider de l'onglet d'accueil et des visites guidées
 * proposées — et **jamais** un droit : `OrgRole`/`PlatformRole` seuls autorisent. À NE PAS utiliser
 * comme contrôle d'accès. Enum **extensible** : ajouter un persona ne casse rien.
 */
export const UserPersona = {
    GENERALIST: 'GENERALIST', // découverte / polyvalent (défaut)
    COLLECTOR: 'COLLECTOR', // collecte de données terrain → accueil Data
    AI_ENGINEER: 'AI_ENGINEER', // entraînement de modèles → accueil Model
    FLEET_MANAGER: 'FLEET_MANAGER', // gestion de flotte terrain → accueil Deploy
    OPERATOR: 'OPERATOR', // opérateur terrain counter-UAS (ADR-0040) → accueil Ops
};
export const userPersonaSchema = z.nativeEnum(UserPersona);
export const USER_PERSONAS = Object.values(UserPersona);
/**
 * Nature d'un événement de consommation (ADR-0025, incrément 2). Le stockage n'y figure pas : il est
 * déjà porté par `Organization.storageUsedBytes`. `AI_TOKENS` compte des jetons (entrée + sortie),
 * `DATA_TRANSFER` des octets *autorisés* à l'émission d'une URL de lecture (borne haute — l'octet ne
 * traverse pas l'API).
 */
export const ConsumptionKind = {
    AI_TOKENS: 'AI_TOKENS',
    DATA_TRANSFER: 'DATA_TRANSFER',
};
export const consumptionKindSchema = z.nativeEnum(ConsumptionKind);
/**
 * Événements d'**analytique d'usage** (ADR-0026, FR-46). Jeu **fermé** : un nom hors de cette liste
 * est rejeté à l'ingestion, jamais stocké — un nom d'événement libre fuirait de la donnée. À NE PAS
 * confondre avec la **télémétrie de vol** du drone (FR-11/FR-16). Ajouter un événement = l'ajouter
 * ici (et l'instrumenter côté front, incrément 2).
 */
export const AnalyticsEventName = {
    PAGE_VIEWED: 'PAGE_VIEWED',
    VIDEO_UPLOAD_STARTED: 'VIDEO_UPLOAD_STARTED',
    VIDEO_UPLOAD_COMPLETED: 'VIDEO_UPLOAD_COMPLETED',
    VIDEO_UPLOAD_FAILED: 'VIDEO_UPLOAD_FAILED',
    VIDEO_PLAYBACK_STARTED: 'VIDEO_PLAYBACK_STARTED',
    ANNOTATION_CREATED: 'ANNOTATION_CREATED',
    SEARCH_PERFORMED: 'SEARCH_PERFORMED',
    ASSISTANT_QUERIED: 'ASSISTANT_QUERIED',
};
export const analyticsEventNameSchema = z.nativeEnum(AnalyticsEventName);
/** Cycle de vie d'une vidéo (PRD FR-10). */
export const VideoStatus = {
    PENDING: 'PENDING',
    UPLOADING: 'UPLOADING',
    UPLOADED: 'UPLOADED',
    PROCESSING: 'PROCESSING',
    READY: 'READY',
    FAILED: 'FAILED',
};
export const videoStatusSchema = z.nativeEnum(VideoStatus);
/** Type d'annotation (PRD FR-25). */
export const AnnotationType = {
    /** Un instant précis. */
    MARKER: 'MARKER',
    /** Un segment [début, fin]. */
    INTERVAL: 'INTERVAL',
    /** Un rectangle sur une frame, en coordonnées normalisées. */
    BBOX: 'BBOX',
};
export const annotationTypeSchema = z.nativeEnum(AnnotationType);
/**
 * Origine d'une annotation. `MODEL` et `HUMAN_VERIFIED` ne sont pas produits en v1 mais
 * existent dès maintenant pour que l'ajout de l'inférence reste un ajout, pas une migration
 * (ADR-0006).
 */
export const AnnotationSource = {
    HUMAN: 'HUMAN',
    MODEL: 'MODEL',
    HUMAN_VERIFIED: 'HUMAN_VERIFIED',
};
export const annotationSourceSchema = z.nativeEnum(AnnotationSource);
/** Portée d'un lien de partage (PRD FR-30). */
export const ShareScope = {
    VIDEO: 'VIDEO',
    MISSION: 'MISSION',
    PROJECT: 'PROJECT',
    IMAGE: 'IMAGE',
};
export const shareScopeSchema = z.nativeEnum(ShareScope);
/** Constructeur du drone, déterminant le parseur de télémétrie (PRD H1). */
export const TelemetryFormat = {
    DJI_SRT: 'DJI_SRT',
    GENERIC_CSV: 'GENERIC_CSV',
};
export const telemetryFormatSchema = z.nativeEnum(TelemetryFormat);
/** Registre de modèles (ADR-0014). Extensibles : TFLite/TensorRT et classification viendront. */
export const ModelFormat = {
    ONNX: 'ONNX',
};
export const modelFormatSchema = z.nativeEnum(ModelFormat);
// Tâche d'un modèle / d'un objectif (ADR-0028). Enum **extensible** : seule `DETECTION` est
// implémentée aujourd'hui ; les suivantes sont **réservées** (nommées, non ouvertes au produit) —
// chacune exigera son pipeline et ses métriques. `TRAJECTORY` (prédiction depuis la télémétrie) est
// une famille inédite qui fera l'objet de son propre ADR.
export const ModelTask = {
    DETECTION: 'DETECTION',
    TRACKING: 'TRACKING',
    TERRAIN: 'TERRAIN',
    TRAJECTORY: 'TRAJECTORY',
};
export const modelTaskSchema = z.nativeEnum(ModelTask);
/** Tâches réellement ouvertes au produit (les autres sont réservées, cf. ADR-0028). */
export const IMPLEMENTED_MODEL_TASKS = [ModelTask.DETECTION];
// --- Flotte de matériels (ADR-0029) ---
// Type d'un matériel de la flotte. Enum **extensible** (même discipline que `ModelTask`) : on nomme
// dès maintenant les familles connues pour qu'un ajout ne demande pas de migration. `COMPUTE` est un
// calculateur de banc (sans airframe), `SENSOR` une charge utile interchangeable, `OTHER` la porte de
// sortie assumée.
export const HardwareAssetKind = {
    DRONE: 'DRONE',
    UGV: 'UGV',
    COMPUTE: 'COMPUTE',
    SENSOR: 'SENSOR',
    OTHER: 'OTHER',
};
export const hardwareAssetKindSchema = z.nativeEnum(HardwareAssetKind);
/** État de parc d'un matériel — un statut d'inventaire, jamais une progression (ADR-0029). */
export const HardwareAssetStatus = {
    ACTIVE: 'ACTIVE', // en service
    BENCH: 'BENCH', // au banc d'essai
    MAINTENANCE: 'MAINTENANCE',
    STOCK: 'STOCK', // en stock, non affecté
};
export const hardwareAssetStatusSchema = z.nativeEnum(HardwareAssetStatus);
// Famille d'un composant : elle **étiquette** le rôle de la pièce et prolonge la séparation
// structurante de l'ADR-0029. `COMPUTE`/`SENSOR` peuvent nourrir l'enveloppe qui adapte le modèle ;
// `AIRFRAME` reste de la métadonnée d'inventaire.
export const HardwareComponentFamily = {
    COMPUTE: 'COMPUTE',
    SENSOR: 'SENSOR',
    AIRFRAME: 'AIRFRAME',
};
export const hardwareComponentFamilySchema = z.nativeEnum(HardwareComponentFamily);
/** Listes ordonnées des valeurs, pour peupler les sélecteurs du front (ordre stable). */
export const HARDWARE_ASSET_KINDS = Object.values(HardwareAssetKind);
export const HARDWARE_ASSET_STATUSES = Object.values(HardwareAssetStatus);
export const HARDWARE_COMPONENT_FAMILIES = Object.values(HardwareComponentFamily);
// --- Catalogue de pièces (ADR-0030) ---
// Catégorie d'une pièce du catalogue. Enum **extensible** : le référentiel s'enrichit au fil des
// nouveautés et des remontées terrain, l'ajout d'une catégorie ne casse rien.
export const HardwareCategory = {
    FRAME: 'FRAME', // châssis
    MOTOR: 'MOTOR',
    ESC: 'ESC',
    FC: 'FC', // contrôleur de vol
    CAMERA: 'CAMERA',
    VTX: 'VTX', // émetteur vidéo
    RX: 'RX', // récepteur radio (liaison de contrôle)
    BATTERY: 'BATTERY',
    PROP: 'PROP', // hélice
    ANTENNA: 'ANTENNA',
    GPS: 'GPS',
    OTHER: 'OTHER',
};
export const hardwareCategorySchema = z.nativeEnum(HardwareCategory);
export const HARDWARE_CATEGORIES = Object.values(HardwareCategory);
/** Cycle de vie d'une référence du catalogue. On **déprécie** (jamais ne supprime) une référence
 *  que des nomenclatures citent encore. */
export const CatalogPartStatus = {
    ACTIVE: 'ACTIVE',
    DEPRECATED: 'DEPRECATED',
};
export const catalogPartStatusSchema = z.nativeEnum(CatalogPartStatus);
export const ModelSource = {
    IMPORTED: 'IMPORTED',
    TRAINED: 'TRAINED',
    // Tiré de la bibliothèque de modèles pré-entraînés (ADR-0019) : instance cloisonnée d'un modèle
    // curé globalement, porteuse de sa provenance.
    ADOPTED: 'ADOPTED',
};
export const modelSourceSchema = z.nativeEnum(ModelSource);
export const ModelStatus = {
    PENDING: 'PENDING',
    READY: 'READY',
    FAILED: 'FAILED',
};
export const modelStatusSchema = z.nativeEnum(ModelStatus);
/** Périmètre d'une évaluation (ADR-0014). Étendu à la session/projet ultérieurement. */
export const EvaluationScope = {
    VIDEO: 'VIDEO',
};
export const evaluationScopeSchema = z.nativeEnum(EvaluationScope);
export const EvaluationStatus = {
    PENDING: 'PENDING',
    RUNNING: 'RUNNING',
    DONE: 'DONE',
    FAILED: 'FAILED',
};
export const evaluationStatusSchema = z.nativeEnum(EvaluationStatus);
/** Actions inscrites au journal d'audit (docs/security.md §7). */
export const AuditAction = {
    LOGIN_SUCCESS: 'LOGIN_SUCCESS',
    LOGIN_FAILURE: 'LOGIN_FAILURE',
    LOGOUT: 'LOGOUT',
    PASSWORD_CHANGED: 'PASSWORD_CHANGED',
    TOTP_ENABLED: 'TOTP_ENABLED',
    TOTP_DISABLED: 'TOTP_DISABLED',
    REFRESH_TOKEN_REUSE_DETECTED: 'REFRESH_TOKEN_REUSE_DETECTED',
    MEMBER_INVITED: 'MEMBER_INVITED',
    MEMBER_REMOVED: 'MEMBER_REMOVED',
    ROLE_CHANGED: 'ROLE_CHANGED',
    VIDEO_CREATED: 'VIDEO_CREATED',
    VIDEO_DELETED: 'VIDEO_DELETED',
    VIDEO_DOWNLOADED: 'VIDEO_DOWNLOADED',
    SHARE_CREATED: 'SHARE_CREATED',
    SHARE_REVOKED: 'SHARE_REVOKED',
    SHARE_ACCESSED: 'SHARE_ACCESSED',
    ANNOTATIONS_EXPORTED: 'ANNOTATIONS_EXPORTED',
    RETENTION_PURGE: 'RETENTION_PURGE',
    ASSISTANT_QUERY: 'ASSISTANT_QUERY',
    // Adoption d'un modèle de la bibliothèque (ADR-0019) : trace qui a tiré quel modèle, sous quelle
    // licence figée. `AuditLog.action` est une colonne texte — l'ajout ne demande pas de migration.
    MODEL_ADOPTED: 'MODEL_ADOPTED',
    // Lancement du rendu d'un lot de simulation (ADR-0021) : trace qui a déclenché une sortie réseau
    // vers le nœud de rendu, et sous quel scénario.
    SIMULATION_LAUNCHED: 'SIMULATION_LAUNCHED',
    // Revue humaine d'un lot rendu (ADR-0023) : qui a validé (ou rejeté) des données synthétiques
    // avant qu'elles n'alimentent l'entraînement. Décision traçable en écriture seule.
    SIMULATION_APPROVED: 'SIMULATION_APPROVED',
    SIMULATION_REJECTED: 'SIMULATION_REJECTED',
    // Console d'administration plateforme (ADR-0025). Chaque franchissement de la frontière
    // d'organisation par un super-administrateur est tracé (PRD §1). `AuditLog.action` est une colonne
    // texte : l'ajout ne demande pas de migration.
    PLATFORM_ORGS_VIEWED: 'PLATFORM_ORGS_VIEWED', // consultation de la liste des organisations
    PLATFORM_ORG_VIEWED: 'PLATFORM_ORG_VIEWED', // consultation du détail d'une organisation
    PLATFORM_MONITORING_VIEWED: 'PLATFORM_MONITORING_VIEWED', // consultation de l'état des services
    PLATFORM_CONSUMPTION_VIEWED: 'PLATFORM_CONSUMPTION_VIEWED', // consultation des consommations
    PLATFORM_ANALYTICS_VIEWED: 'PLATFORM_ANALYTICS_VIEWED', // consultation de l'analytique d'usage
    PLATFORM_ADMIN_GRANTED: 'PLATFORM_ADMIN_GRANTED', // attribution d'un rôle plateforme (hors bande)
    // Bascule du consentement au partage de l'état d'architecture avec l'éditeur (ADR-0037 §4).
    // Chaque activation/révocation est tracée (souveraineté). Colonne texte : aucun changement de schéma.
    ORG_HEALTH_CONSENT_CHANGED: 'ORG_HEALTH_CONSENT_CHANGED',
};
export const auditActionSchema = z.nativeEnum(AuditAction);
// --- Simulation de données d'entraînement (ADR-0021) ---
/** Type d'environnement rendu : pilote décor HDRI, végétation et bâti. */
export const SimulationEnvironment = {
    FIELD: 'FIELD', // champ / rase campagne
    FOREST: 'FOREST', // forêt / couvert dense
    DENSE_HABITAT: 'DENSE_HABITAT', // habitat dense (urbain)
    PERIURBAN: 'PERIURBAN', // périurbain / lotissement
};
export const simulationEnvironmentSchema = z.nativeEnum(SimulationEnvironment);
/** Moment de la journée : pilote l'élévation solaire et la température de couleur. */
export const SimulationTimeOfDay = {
    DAWN: 'DAWN', // aube / petit matin
    DAY: 'DAY', // plein jour
    DUSK: 'DUSK', // crépuscule
    NIGHT: 'NIGHT', // nuit
};
export const simulationTimeOfDaySchema = z.nativeEnum(SimulationTimeOfDay);
/** Couverture nuageuse / météo : dureté des ombres, contraste, portée visuelle. */
export const SimulationSky = {
    CLEAR: 'CLEAR', // ciel dégagé / ensoleillé
    PARTLY_CLOUDY: 'PARTLY_CLOUDY',
    OVERCAST: 'OVERCAST', // couvert
    FOG: 'FOG', // brouillard / brume
};
export const simulationSkySchema = z.nativeEnum(SimulationSky);
/**
 * Source de l'environnement de rendu (ADR-0023, incrément C). `PRESET` : décor générique
 * (environnement/moment/ciel). `RECONSTRUCTION` : scène reconstruite en 3D à partir de médias de la
 * zone déjà sur la plateforme (photogrammétrie / gaussian splatting au nœud GPU, dormant).
 */
export const SimulationEnvironmentSource = {
    PRESET: 'PRESET',
    RECONSTRUCTION: 'RECONSTRUCTION',
};
export const simulationEnvironmentSourceSchema = z.nativeEnum(SimulationEnvironmentSource);
/** Nature d'un média de référence pour la reconstruction (ADR-0023). */
export const SimulationMediaKind = {
    VIDEO: 'VIDEO',
    IMAGE: 'IMAGE',
};
export const simulationMediaKindSchema = z.nativeEnum(SimulationMediaKind);
/**
 * Type **physique** d'une menace aérienne (ADR-0023). **Figé côté contrat** : il pilote le modèle 3D
 * et la cinématique par défaut au rendu — le moteur ne sait instancier que des types connus. Le
 * **nom de classe** appris par le modèle, lui, vient du référentiel de l'organisation (voir
 * `simulationThreatSchema.label`).
 */
export const SimulationThreatType = {
    FPV: 'FPV', // FPV de course / kamikaze
    FIXED_WING: 'FIXED_WING', // voilure fixe
    QUAD: 'QUAD', // multirotor de type quadricoptère
    VTOL: 'VTOL', // décollage vertical / hybride
};
export const simulationThreatTypeSchema = z.nativeEnum(SimulationThreatType);
/** Forme de trajectoire de la menace dans la scène (ADR-0023). */
export const SimulationThreatTrajectory = {
    DIVE: 'DIVE', // piqué sur la zone
    PASS: 'PASS', // passage rectiligne
    ORBIT: 'ORBIT', // orbite autour de la zone
    DISPERSE: 'DISPERSE', // dispersion d'essaim (caps/vitesses variés)
    HOVER: 'HOVER', // vol stationnaire / loiter
};
export const simulationThreatTrajectorySchema = z.nativeEnum(SimulationThreatTrajectory);
/** Axe d'arrivée de la menace, relatif au point de vue (ADR-0023). */
export const SimulationThreatApproach = {
    FRONTAL: 'FRONTAL',
    LATERAL: 'LATERAL',
    VERTICAL: 'VERTICAL',
    MIXED: 'MIXED', // directions multiples (typiquement un essaim)
};
export const simulationThreatApproachSchema = z.nativeEnum(SimulationThreatApproach);
/**
 * Étape de vie d'un lot de simulation. Le rendu se faisant hors plateforme (nœud GPU, ADR-0021),
 * un état d'attente `QUEUED` distinct sépare la demande de la disponibilité.
 */
export const SimulationStatus = {
    DRAFT: 'DRAFT', // scénario écrit, pas encore lancé
    QUEUED: 'QUEUED', // lancé, en attente du nœud de rendu
    RENDERING: 'RENDERING',
    READY: 'READY', // rendu, en attente de validation humaine (ADR-0023)
    APPROVED: 'APPROVED', // validé : exploitable pour l'entraînement
    REJECTED: 'REJECTED', // rejeté à la revue : ne doit pas alimenter l'entraînement
    FAILED: 'FAILED',
};
export const simulationStatusSchema = z.nativeEnum(SimulationStatus);
/** Listes ordonnées des valeurs, pour peupler les sélecteurs côté front (ordre stable). */
export const SIMULATION_ENVIRONMENTS = Object.values(SimulationEnvironment);
export const SIMULATION_TIMES_OF_DAY = Object.values(SimulationTimeOfDay);
export const SIMULATION_SKIES = Object.values(SimulationSky);
export const SIMULATION_THREAT_TYPES = Object.values(SimulationThreatType);
export const SIMULATION_THREAT_TRAJECTORIES = Object.values(SimulationThreatTrajectory);
export const SIMULATION_THREAT_APPROACHES = Object.values(SimulationThreatApproach);
// --- Packaging de modèle : déploiement edge (ADR-0034) ---
// Runtime cible du binaire empaqueté. Enum **extensible** (même discipline que `ModelTask`) : le bon
// format dépend de l'accélérateur de la cible, et d'autres runtimes (RKNN, OpenVINO…) s'ajouteront
// sans rupture. Stocké en **texte** côté base — le paquet vit hors du VPS (nœud de conversion).
export const PackagingRuntime = {
    ONNX: 'ONNX', // repli universel (CPU / SoC générique)
    TFLITE: 'TFLITE', // Edge TPU (Coral) / Hailo
    TENSORRT: 'TENSORRT', // NVIDIA / Jetson (Orin, Xavier)
};
export const packagingRuntimeSchema = z.nativeEnum(PackagingRuntime);
// Quantification appliquée à la conversion. Enum **extensible** : d'autres schémas (INT4…) viendront.
export const PackagingQuantization = {
    NONE: 'NONE', // pleine précision (FP32)
    FP16: 'FP16', // demi-précision
    INT8: 'INT8', // quantification entière (calibration au nœud de conversion)
};
export const packagingQuantizationSchema = z.nativeEnum(PackagingQuantization);
/**
 * Étape de vie d'un paquet de modèle. La conversion/quantification se faisant hors plateforme
 * (nœud de conversion, ADR-0034), un état d'attente `QUEUED` distinct sépare la demande de la
 * disponibilité — comme pour la simulation. Rejouable : relancer un `FAILED` repasse `QUEUED`.
 */
export const PackagingStatus = {
    DRAFT: 'DRAFT', // paquet écrit, pas encore lancé
    QUEUED: 'QUEUED', // lancé, en attente du nœud de conversion
    BUILDING: 'BUILDING',
    READY: 'READY', // binaire produit et déposé
    FAILED: 'FAILED',
};
export const packagingStatusSchema = z.nativeEnum(PackagingStatus);
/** Listes ordonnées des valeurs, pour peupler les sélecteurs côté front (ordre stable). */
export const PACKAGING_RUNTIMES = Object.values(PackagingRuntime);
export const PACKAGING_QUANTIZATIONS = Object.values(PackagingQuantization);
//# sourceMappingURL=enums.js.map